import { DestroyRef, computed, effect, inject, Service, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  form,
  maxLength,
  pattern,
  required,
  maxLengthError,
  requiredError,
  validate,
} from '@angular/forms/signals';
import { cpfValido } from '../../../../shared/utils/documento.util';
import { DesafioDoFormulario, DesafioFormularioService } from '../../services/desafio-formulario.service';
import { MeusDadosService } from '../../services/meus-dados.service';
import { PedidoSicPublicoService } from '../../services/pedido-sic-publico.service';
import {
  DepartamentoSicPublicoResponse,
  PedidoSicAberturaResponse,
} from '../../models/pedido-sic.model';

/**
 * Etapas do pedido. Identificadores em vez de índices porque a identificação sai do fluxo no
 * pedido anônimo e os anexos saem quando o departamento não os aceita — as posições deixam de
 * ser fixas.
 */
export type EtapaId =
  | 'orientacoes'
  | 'identificacao'
  | 'dados'
  | 'anexos'
  | 'confirmacao';

/** Como a pessoa chegou até o formulário — define o que é exigido nas etapas seguintes. */
export type ModoAcesso = '' | 'identificado' | 'anonimo';

export interface IdentificacaoSic {
  nomeCompleto: string;
  cpf: string;
  telefone: string;
  email: string;
  /** A CHAVE do catálogo (`PessoaFisica`…). Sem valor inicial: quem escolhe é o cidadão. */
  tipoSolicitante: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  uf: string;
  cep: string;
}

export interface DadosPedidoSic {
  /** A CHAVE do catálogo (`SistemaEmail`…). */
  formaRecebimento: string;
  /** O identificador — o nome não viaja. `null` até a escolha. */
  codDepartamento: number | null;
  assunto: string;
  descricao: string;
  receberAvisos: boolean;
  temProtocoloAnterior: boolean;
  protocoloAnterior: string;
}

/** Um anexo escolhido — com o ARQUIVO, que é o que vai no envio. */
export interface AnexoSic {
  id: number;
  nome: string;
  tamanho: number;
  arquivo: File;
}

/** Os erros de validação que o servidor devolveu, já separados pela etapa a que pertencem. */
export type ErrosServidorSic = Partial<Record<EtapaId, readonly string[]>>;

export const LIMITE_PALAVRAS_DESCRICAO = 500;

export const LIMITE_CARACTERES_ASSUNTO = 250;

/**
 * Valores de PARTIDA da etapa de anexos — valem só até o departamento ser escolhido. Depois,
 * quem manda é a configuração do departamento, e o servidor a revalida no envio.
 */
export const LIMITE_ANEXOS = 5;

export const TAMANHO_MAXIMO_ANEXO = 5 * 1024 * 1024;

export const FORMATOS_ANEXO: readonly string[] = ['pdf', 'jpg', 'jpeg', 'png'];

/** O mesmo teto do back-end para o nome do arquivo. */
export const TAMANHO_MAXIMO_NOME_ARQUIVO = 260;

/** Item de catálogo: a chave vai para a API, o rótulo para a tela. */
export interface OpcaoSic {
  chave: string;
  rotulo: string;
}

export const FORMAS_RECEBIMENTO: readonly OpcaoSic[] = [
  { chave: 'SistemaEmail', rotulo: 'Pelo Sistema / e-mail' },
  { chave: 'CorrespondenciaFisica', rotulo: 'Correspondência física (com custo de postagem)' },
  { chave: 'RetiradaPresencial', rotulo: 'Retirada presencial no SIC' },
  { chave: 'ConsultaPresencial', rotulo: 'Consulta presencial ao documento' },
];

export const TIPOS_SOLICITANTE: readonly OpcaoSic[] = [
  { chave: 'PessoaFisica', rotulo: 'Pessoa física' },
  { chave: 'PessoaJuridica', rotulo: 'Pessoa jurídica' },
  { chave: 'Imprensa', rotulo: 'Imprensa' },
  { chave: 'OrgaoPublico', rotulo: 'Órgão público' },
  { chave: 'EntidadeSociedadeCivil', rotulo: 'Entidade da sociedade civil' },
];

export const UNIDADES_FEDERATIVAS: readonly string[] = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
];

const MENSAGEM_OBRIGATORIO = 'Campo obrigatório.';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const IDENTIFICACAO_VAZIA: IdentificacaoSic = {
  nomeCompleto: '',
  cpf: '',
  telefone: '',
  email: '',
  tipoSolicitante: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  uf: '',
  cep: '',
};

const DADOS_PEDIDO_VAZIOS: DadosPedidoSic = {
  formaRecebimento: '',
  codDepartamento: null,
  assunto: '',
  descricao: '',
  receberAvisos: true,
  temProtocoloAnterior: false,
  protocoloAnterior: '',
};

/** Conta palavras separadas por qualquer espaço em branco — a mesma regra do back-end. */
export function contarPalavras(texto: string): number {
  const limpo = texto.trim();
  return limpo ? limpo.split(/\s+/).length : 0;
}

export function rotuloDe(catalogo: readonly OpcaoSic[], chave: string): string {
  return catalogo.find((opcao) => opcao.chave === chave)?.rotulo ?? chave;
}

/** Os limites de anexo em vigor: do departamento escolhido, ou os valores de partida. */
export interface LimitesAnexoSic {
  quantidade: number;
  tamanhoBytes: number;
  extensoes: readonly string[];
}

/**
 * Por que um arquivo não cabe nos limites, ou `null` quando cabe. Função pura: é a mesma regra
 * na seleção do arquivo e na reavaliação depois de trocar o departamento.
 */
export function motivoRecusaAnexo(arquivo: { name: string; size: number }, limites: LimitesAnexoSic): string | null {
  const extensao = arquivo.name.split('.').pop()?.toLowerCase() ?? '';

  if (!limites.extensoes.includes(extensao)) return `${arquivo.name} — formato não aceito pelo departamento.`;

  if (arquivo.size > limites.tamanhoBytes) {
    return `${arquivo.name} — acima de ${Math.round(limites.tamanhoBytes / (1024 * 1024))} MB.`;
  }

  if (arquivo.name.length > TAMANHO_MAXIMO_NOME_ARQUIVO) {
    return `O nome do arquivo passa de ${TAMANHO_MAXIMO_NOME_ARQUIVO} caracteres. Renomeie e tente novamente.`;
  }

  return null;
}

/**
 * Guarda o pedido enquanto a pessoa navega pelas etapas. Vive no componente do fluxo
 * (`providers`), e não na raiz, porque cada etapa é recriada a cada troca de passo e os dados
 * precisam sobreviver às idas e vindas — inclusive para a etapa de revisão.
 */
@Service({ autoProvided: false })
export class PedidoSicStore {
  private readonly meusDados = inject(MeusDadosService);
  private readonly publico = inject(PedidoSicPublicoService);
  private readonly destroyRef = inject(DestroyRef);

  readonly modoAcesso = signal<ModoAcesso>('');

  /** Login feito dentro do fluxo; sessão vinda de outra tela não pula a escolha daqui. */
  readonly sessaoIniciada = signal(false);

  /** Vai no envio: o checkbox da tela não é garantia, e o servidor exige o aceite. */
  readonly orientacoesAceitas = signal(false);

  readonly anonimo = computed(() => this.modoAcesso() === 'anonimo');

  readonly identificado = computed(() => this.modoAcesso() === 'identificado');

  readonly identificacao = signal<IdentificacaoSic>({ ...IDENTIFICACAO_VAZIA });

  /** O pré-preenchimento acontece uma vez por entrada: depois, o que vale é o que o cidadão revisou. */
  private identificacaoPreenchida = false;

  readonly identificacaoForm = form(this.identificacao, (caminho) => {
    required(caminho.nomeCompleto, { message: MENSAGEM_OBRIGATORIO });
    required(caminho.cpf, { message: MENSAGEM_OBRIGATORIO });
    validate(caminho.cpf, ({ value }) =>
      !value() || cpfValido(value()) ? null : requiredError({ message: 'Informe um CPF válido.' })
    );
    required(caminho.email, { message: MENSAGEM_OBRIGATORIO });
    pattern(caminho.email, EMAIL, { message: 'Informe um e-mail válido.' });
    required(caminho.tipoSolicitante, { message: 'Escolha o tipo de solicitante.' });

    // Decisão 12: sem os três, não há para onde mandar a correspondência.
    const correspondencia = () => this.dadosPedido().formaRecebimento === 'CorrespondenciaFisica';
    const mensagemEndereco = 'Obrigatório para receber por correspondência física.';
    required(caminho.logradouro, { message: mensagemEndereco, when: correspondencia });
    required(caminho.numero, { message: mensagemEndereco, when: correspondencia });
    required(caminho.bairro, { message: mensagemEndereco, when: correspondencia });
  });

  readonly dadosPedido = signal<DadosPedidoSic>({ ...DADOS_PEDIDO_VAZIOS });

  readonly dadosPedidoForm = form(this.dadosPedido, (caminho) => {
    // No pedido anônimo não há canal de retorno, então o campo nem aparece nem é exigido.
    required(caminho.formaRecebimento, {
      message: MENSAGEM_OBRIGATORIO,
      when: () => !this.anonimo(),
    });
    required(caminho.codDepartamento, { message: MENSAGEM_OBRIGATORIO });
    required(caminho.assunto, { message: MENSAGEM_OBRIGATORIO });
    maxLength(caminho.assunto, LIMITE_CARACTERES_ASSUNTO, {
      message: `O assunto deve ter no máximo ${LIMITE_CARACTERES_ASSUNTO} caracteres.`,
    });
    required(caminho.descricao, { message: MENSAGEM_OBRIGATORIO });
    validate(caminho.descricao, ({ value }) =>
      contarPalavras(value()) > LIMITE_PALAVRAS_DESCRICAO
        ? maxLengthError(LIMITE_PALAVRAS_DESCRICAO, {
            message: `A descrição deve ter no máximo ${LIMITE_PALAVRAS_DESCRICAO} palavras.`,
          })
        : null
    );
    maxLength(caminho.protocoloAnterior, 30, {
      message: 'O protocolo deve ter no máximo 30 caracteres.',
    });
    validate(caminho.protocoloAnterior, ({ value, valueOf }) =>
      valueOf(caminho.temProtocoloAnterior) && !this.anonimo() && !value().trim()
        ? requiredError({ message: 'Informe o protocolo vinculado.' })
        : null
    );
  });

  readonly anexos = signal<readonly AnexoSic[]>([]);

  // --------------------------------------------------------- departamentos
  /** Os departamentos que a sessão pode escolher, como o servidor os devolveu. */
  readonly departamentos = signal<readonly DepartamentoSicPublicoResponse[]>([]);

  readonly carregandoDepartamentos = signal(false);

  readonly falhaDepartamentos = signal(false);

  readonly departamentoEscolhido = computed(() =>
    this.departamentos().find((dep) => dep.codDepartamentoDep === this.dadosPedido().codDepartamento) ?? null
  );

  /**
   * A etapa de anexos existe enquanto o departamento não foi escolhido (valores de partida) e
   * some quando o escolhido não os aceita.
   */
  readonly permiteAnexos = computed(() => this.departamentoEscolhido()?.permiteAnexos ?? true);

  readonly limitesAnexo = computed<LimitesAnexoSic>(() => {
    const dep = this.departamentoEscolhido();

    if (!dep?.permiteAnexos) {
      return { quantidade: LIMITE_ANEXOS, tamanhoBytes: TAMANHO_MAXIMO_ANEXO, extensoes: FORMATOS_ANEXO };
    }

    return {
      quantidade: dep.quantidadeMaximaAnexos ?? LIMITE_ANEXOS,
      tamanhoBytes: (dep.tamanhoMaximoAnexoMb ?? 5) * 1024 * 1024,
      extensoes: dep.extensoesAnexo.map((extensao) => extensao.toLowerCase()),
    };
  });

  /**
   * A reavaliação dos anexos já escolhidos contra os limites EM VIGOR — trocar para um
   * departamento com limites menores não deixa passar o que foi aceito antes.
   */
  readonly pendenciasAnexos = computed<readonly string[]>(() => {
    const anexos = this.anexos();

    if (anexos.length === 0) return [];

    if (!this.permiteAnexos()) {
      return [`O departamento escolhido não aceita anexos. Remova os ${anexos.length} arquivo(s) para enviar.`];
    }

    const limites = this.limitesAnexo();
    const pendencias = anexos
      .map((anexo) => motivoRecusaAnexo({ name: anexo.nome, size: anexo.tamanho }, limites))
      .filter((motivo): motivo is string => motivo !== null);

    if (anexos.length > limites.quantidade) {
      pendencias.unshift(`O departamento aceita no máximo ${limites.quantidade} arquivo(s) por pedido.`);
    }

    return pendencias;
  });

  // ------------------------------------------------------------- envio
  readonly enviando = signal(false);

  /** O 201 do servidor — é ele que a tela final e o comprovante exibem. */
  readonly resultado = signal<PedidoSicAberturaResponse | null>(null);

  readonly errosServidor = signal<ErrosServidorSic>({});

  /**
   * O desafio de formulário e o honeypot deste pedido (change anti-abuso-superficie-publica):
   * pedido ao abrir o wizard, enviado no cabeçalho do POST, descartado no recomeçar.
   */
  readonly desafio = new DesafioDoFormulario(inject(DesafioFormularioService));

  readonly palavrasDescricao = computed(() => contarPalavras(this.dadosPedido().descricao));

  readonly identificacaoValida = computed(() => this.identificacaoForm().valid());

  readonly dadosPedidoValidos = computed(() => this.dadosPedidoForm().valid());

  /** Só há o que cancelar depois que algum dado do pedido foi informado. */
  readonly preenchido = computed(() => {
    const identificacao = this.identificacao();
    const pedido = this.dadosPedido();

    const identificacaoAlterada = (Object.keys(identificacao) as (keyof IdentificacaoSic)[]).some(
      (campo) => identificacao[campo].trim() !== IDENTIFICACAO_VAZIA[campo]
    );

    const pedidoAlterado =
      pedido.formaRecebimento !== '' ||
      pedido.codDepartamento !== null ||
      pedido.assunto.trim() !== '' ||
      pedido.descricao.trim() !== '' ||
      pedido.temProtocoloAnterior ||
      pedido.protocoloAnterior.trim() !== '';

    return identificacaoAlterada || pedidoAlterado || this.anexos().length > 0;
  });

  constructor() {
    // O cadastro chega depois do login (o `MeusDadosService` só busca com sessão): quando ele
    // aparecer, a identificação ainda intocada é preenchida com ele, uma vez.
    effect(() => {
      const perfil = this.meusDados.perfil();

      if (perfil && this.identificado() && !this.identificacaoPreenchida) {
        this.preencherComPerfil();
      }
    });
  }

  /**
   * Traz o cadastro REAL do cidadão logado (`GET sai/core/MeusDados`) para a identificação —
   * contato e endereço, com o bairro. O tipo de solicitante fica em branco: quem escolhe é ele.
   * Nascimento e gênero NÃO entram: o servidor os copia do cadastro.
   */
  preencherComPerfil(): void {
    const cadastro = this.meusDados.perfil();

    if (!cadastro) return;

    this.identificacaoPreenchida = true;
    this.identificacao.set({
      nomeCompleto: cadastro.nomeCompleto,
      cpf: cadastro.cpf,
      telefone: cadastro.telefoneCelular01 || cadastro.telefoneResidencial,
      email: cadastro.email,
      tipoSolicitante: '',
      logradouro: cadastro.enderecoCompleto,
      numero: cadastro.numero,
      complemento: cadastro.complemento,
      bairro: cadastro.bairro,
      cidade: cadastro.cidade,
      uf: cadastro.estado,
      cep: cadastro.cep,
    });
  }

  entrarIdentificado(): void {
    this.entrarPorGov();
    this.preencherComPerfil();
  }

  /** Acesso gov.br: identifica o pedido, mas sem cadastro do portal para preencher. */
  entrarPorGov(): void {
    this.modoAcesso.set('identificado');
    this.dadosPedido.update((atual) => ({ ...atual, receberAvisos: true }));
    this.carregarDepartamentos();
  }

  entrarAnonimo(): void {
    this.modoAcesso.set('anonimo');
    this.dadosPedido.update((atual) => ({
      ...atual,
      formaRecebimento: '',
      receberAvisos: false,
      temProtocoloAnterior: false,
      protocoloAnterior: '',
    }));
    this.carregarDepartamentos();
  }

  /**
   * A lista é da SESSÃO: no anônimo vem sem os departamentos que exigem login. Recarrega a cada
   * troca de caminho, e o departamento escolhido que não estiver mais na lista é desfeito.
   */
  carregarDepartamentos(): void {
    this.carregandoDepartamentos.set(true);
    this.falhaDepartamentos.set(false);

    this.publico
      .departamentos(this.anonimo())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (lista) => {
          this.departamentos.set(lista);
          this.carregandoDepartamentos.set(false);

          const escolhido = this.dadosPedido().codDepartamento;

          if (escolhido !== null && !lista.some((dep) => dep.codDepartamentoDep === escolhido)) {
            this.dadosPedido.update((atual) => ({ ...atual, codDepartamento: null }));
          }
        },
        error: () => {
          this.departamentos.set([]);
          this.carregandoDepartamentos.set(false);
          this.falhaDepartamentos.set(true);
        },
      });
  }

  limpar(): void {
    this.modoAcesso.set('');
    this.sessaoIniciada.set(false);
    this.orientacoesAceitas.set(false);
    this.identificacao.set({ ...IDENTIFICACAO_VAZIA });
    this.identificacaoPreenchida = false;
    this.dadosPedido.set({ ...DADOS_PEDIDO_VAZIOS });
    this.anexos.set([]);
    this.departamentos.set([]);
    this.resultado.set(null);
    this.errosServidor.set({});
    this.enviando.set(false);

    // O próximo pedido pede o seu desafio.
    this.desafio.descartar();
    this.desafio.carregar();
  }
}
